import React, { useState, useRef, useEffect } from 'react';
import { Tag } from '@/types';
import { Plus, X, Star, Trash2, MoreHorizontal, Wand2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  tags: Tag[];
  selectedTagId: string | null;
  onSelectTag: (id: string | null) => void;
  onAddTag: (tag: Tag) => void;
  onDeleteTag: (id: string) => void;
  onReorderTags: (tags: Tag[]) => void;
  onCleanTags?: () => void;
  bookmarksCount?: Record<string, number>;
  onEditTag: (tag: Tag) => void;
  onUpdateAllTagsColor?: (color: string) => void;
}

const COLORS = [
    '#ef4444', // Red
    '#f97316', // Orange
    '#f59e0b', // Amber
    '#84cc16', // Lime
    '#10b981', // Emerald
    '#06b6d4', // Cyan
    '#3b82f6', // Blue
    '#6366f1', // Indigo
    '#8b5cf6', // Violet
    '#d946ef', // Fuchsia
    '#f43f5e', // Rose
    '#64748b', // Slate
    '#71717a', // Zinc
    '#737373', // Neutral
    '#78716c', // Stone
    '#a8a29e', // Warm Gray
];

const getColorWithOpacity = (color: string, alpha: number) => {
    if (!color) return 'transparent';
    
    // Handle Hex
    if (color.startsWith('#')) {
        // Expand shorthand hex if needed (e.g. #F00 -> #FF0000) - though we usually use full hex
        const hex = color.length === 4 ? '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3] : color;
        
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        
        if (isNaN(r) || isNaN(g) || isNaN(b)) return color;
        
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } 
    
    // Handle HSL
    if (color.startsWith('hsl')) {
        return color.replace('hsl', 'hsla').replace(')', `, ${alpha})`);
    }
    
    // Handle RGB
    if (color.startsWith('rgb(')) {
        return color.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
    }

    return color;
};

const SortableTagItem = ({ tag, selectedTagId, onSelectTag, onDeleteTag, count, onEditTag }: { tag: Tag, selectedTagId: string | null, onSelectTag: (id: string | null) => void, onDeleteTag: (id: string) => void, count?: number, onEditTag: (tag: Tag) => void }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: tag.id });

    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(tag.name);
    const [editColor, setEditColor] = useState(tag.color);

    const handleSelectTag = (id: string | null) => {
      onSelectTag(id);
    };

    const handleEditConfirm = () => {
        if (editName.trim() && (editName !== tag.name || editColor !== tag.color)) {
            onEditTag({ ...tag, name: editName.trim(), color: editColor });
        }
        setIsEditing(false);
    };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : 1,
    };

    if (isEditing) {
        return (
            <div ref={setNodeRef} style={style} className="flex items-center gap-1 bg-white dark:bg-slate-800 p-1 rounded-full border border-blue-500 shadow-lg z-20">
                <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditConfirm();
                        if (e.key === 'Escape') setIsEditing(false);
                    }}
                    autoFocus
                    className="px-2 py-0.5 text-sm bg-transparent outline-none w-20 text-slate-900 dark:text-white"
                />
                <div className="flex gap-0.5">
                    {COLORS.slice(0, 8).map(c => (
                        <button 
                            key={c}
                            className={`w-3 h-3 rounded-full transition-transform hover:scale-125 ${editColor === c ? 'ring-1 ring-offset-1 ring-slate-400' : ''}`}
                            style={{ backgroundColor: c }}
                            onClick={() => setEditColor(c)}
                        />
                    ))}
                    <button 
                        className="w-3 h-3 rounded-full bg-gradient-to-br from-white to-slate-200 border border-slate-300 flex items-center justify-center text-[8px] hover:scale-110"
                        title="更多颜色"
                        onClick={(e) => {
                             const random = generateRandomColor();
                             setEditColor(random);
                        }}
                    >
                        +
                    </button>
                </div>
                <button onClick={handleEditConfirm} className="p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-green-500">
                    <Plus size={12} />
                </button>
                <button onClick={() => setIsEditing(false)} className="p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-slate-400">
                    <X size={12} />
                </button>
            </div>
        );
    }

    const isSelected = selectedTagId === tag.id;

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <button
                onClick={() => onSelectTag(tag.id)}
                onDoubleClick={() => {
                    setEditName(tag.name);
                    setEditColor(tag.color);
                    setIsEditing(true);
                }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 border ${
                    isSelected
                    ? 'ring-2 ring-offset-1 dark:ring-offset-slate-900 border-transparent'
                    : 'hover:opacity-80'
                }`}
                style={{
                    backgroundColor: isSelected ? tag.color : getColorWithOpacity(tag.color, 0.15),
                    color: isSelected ? '#fff' : 'inherit',
                    borderColor: isSelected ? 'transparent' : tag.color,
                }}
            >
                <span className={`${isSelected ? 'text-white' : 'text-slate-900 dark:text-slate-200'}`}>
                    {tag.name}
                </span>
                <span className={`text-[10px] ml-0.5 px-1 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                    {count || 0}
                </span>
                {isSelected && (
                    <span onClick={(e) => { e.stopPropagation(); onDeleteTag(tag.id); onSelectTag(null); }} className="hover:bg-white/20 rounded-full p-0.5 ml-0.5 text-white">
                        <X size={10} />
                    </span>
                )}
            </button>
        </div>
    );
};

const generateRandomColor = () => {
    // Generate a vibrant random color
    const hue = Math.floor(Math.random() * 360);
    const saturation = 70 + Math.floor(Math.random() * 30); // 70-100%
    const lightness = 45 + Math.floor(Math.random() * 15); // 45-60%
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const TagManager = ({ tags, selectedTagId, onSelectTag, onAddTag, onDeleteTag, onReorderTags, onCleanTags, bookmarksCount, onEditTag, onUpdateAllTagsColor }: Props) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(COLORS[6]); // Default blue
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(event.target as Node)) {
        setShowTools(false);
        setShowColorPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRandomizeColors = () => {
    if (onUpdateAllTagsColor) {
        // We need a way to pass multiple updates or handle random logic in parent.
        // Or we iterate here and call update for each? No, `onUpdateAllTagsColor` sets one color for all.
        // The user wants "randomly set colors for all tags", meaning each tag gets a different random color.
        // We need a new prop for this or reuse `onReorderTags` (since it saves tags) to update colors.
        
        // Let's implement logic to generate random colors and update tags locally then save.
        // Since we don't have `setTags` here directly but we have `onReorderTags` which accepts `Tag[]` and saves them.
        // We can use `onReorderTags` to save the updated tags with new colors!
        
        const updatedTags = tags.map(t => {
            if (t.name === '推荐首页') return t;
            return { ...t, color: generateRandomColor() };
        });
        
        onReorderTags(updatedTags); // This saves the tags to storage
        setShowTools(false);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAddTag = () => {
    if (newTagName.trim()) {
      onAddTag({
        id: uuidv4(),
        name: newTagName.trim(),
        color: selectedColor
      });
      setNewTagName('');
      setIsAdding(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
        const oldIndex = tags.findIndex((tag) => tag.id === active.id);
        const newIndex = tags.findIndex((tag) => tag.id === over.id);
        // Ensure "Recommended Home" is not involved or handled correctly if passed in tags
        // But we will filter it out from SortableContext
        
        // However, the `tags` prop includes "Recommended Home".
        // If we only sort the subset, we need to reconstruct the full list.
        
        const draggableTags = tags.filter(t => t.name !== '推荐首页');
        const oldDraggableIndex = draggableTags.findIndex(t => t.id === active.id);
        const newDraggableIndex = draggableTags.findIndex(t => t.id === over.id);
        
        const newDraggableTags = arrayMove(draggableTags, oldDraggableIndex, newDraggableIndex);
        
        // Reconstruct full list: Recommended Home + New Draggable Tags
        const recommendedTag = tags.find(t => t.name === '推荐首页');
        const finalTags = recommendedTag ? [recommendedTag, ...newDraggableTags] : newDraggableTags;
        
        onReorderTags(finalTags);
    }
  };

  const recommendedTag = tags.find(t => t.name === '推荐首页');
  const draggableTags = tags.filter(t => t.name !== '推荐首页');

  return (
    <div className="flex flex-wrap items-center gap-2 mb-8 py-2 relative">
      <button
        onClick={() => onSelectTag(null)}
        className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
          selectedTagId === null
            ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 dark:hover:bg-slate-750'
        }`}
      >
        全部
        <span className={`text-[10px] ml-0.5 px-1 rounded-full ${selectedTagId === null ? 'bg-white/20 text-white' : 'bg-black/5 text-slate-500 dark:bg-white/10 dark:text-slate-400'}`}>
            {bookmarksCount?.['all'] || 0}
        </span>
      </button>

      {/* Static Recommended Home Tag */}
      {recommendedTag && (
        <button
          key={recommendedTag.id}
          onClick={() => onSelectTag(recommendedTag.id)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 border ${
            selectedTagId === recommendedTag.id
              ? 'ring-2 ring-offset-1 dark:ring-offset-slate-900 border-transparent'
              : 'hover:opacity-80'
          }`}
          style={{
            backgroundColor: selectedTagId === recommendedTag.id ? recommendedTag.color : getColorWithOpacity(recommendedTag.color, 0.15),
            color: selectedTagId === recommendedTag.id ? '#fff' : 'inherit',
            borderColor: selectedTagId === recommendedTag.id ? 'transparent' : recommendedTag.color,
          }}
        >
          <Star size={10} fill={selectedTagId === recommendedTag.id ? 'white' : recommendedTag.color} className={selectedTagId === recommendedTag.id ? 'text-white' : ''} style={{ color: selectedTagId === recommendedTag.id ? 'white' : recommendedTag.color }} />
          <span className={`${selectedTagId === recommendedTag.id ? 'text-white' : 'text-slate-900 dark:text-slate-200'}`}>
            {recommendedTag.name}
          </span>
          <span className={`text-[10px] ml-0.5 px-1 rounded-full ${selectedTagId === recommendedTag.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
              {bookmarksCount?.[recommendedTag.id] || 0}
          </span>
        </button>
      )}
      
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={draggableTags.map(t => t.id)} strategy={horizontalListSortingStrategy}>
            {draggableTags.map(tag => (
                <SortableTagItem 
                    key={tag.id} 
                    tag={tag} 
                    selectedTagId={selectedTagId} 
                    onSelectTag={onSelectTag} 
                    onDeleteTag={onDeleteTag} 
                    count={bookmarksCount?.[tag.id]}
                    onEditTag={onEditTag}
                />
            ))}
        </SortableContext>
      </DndContext>

      {isAdding ? (
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 p-1.5 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm animate-in fade-in slide-in-from-left-4 duration-200">
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            placeholder="标签名称"
            className="pl-3 py-1 text-sm bg-transparent outline-none w-24 dark:text-white"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
          />
          <div className="flex gap-1">
             {COLORS.slice(0, 8).map(c => (
                 <button 
                    key={c} 
                    className={`w-4 h-4 rounded-full ${selectedColor === c ? 'ring-2 ring-offset-1 dark:ring-offset-slate-800' : ''}`} 
                    style={{ backgroundColor: c }}
                    onClick={() => setSelectedColor(c)}
                 />
             ))}
             <button 
                className={`w-4 h-4 rounded-full bg-gradient-to-br from-white to-slate-200 border border-slate-300 flex items-center justify-center text-[10px] hover:scale-110 ${COLORS.slice(8).includes(selectedColor) ? 'ring-2 ring-offset-1 dark:ring-offset-slate-800 ring-blue-500' : ''}`}
                title="随机其他颜色"
                onClick={() => {
                    const random = generateRandomColor();
                    setSelectedColor(random);
                }}
             >
                <Wand2 size={8} />
             </button>
          </div>
          <button onClick={handleAddTag} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-green-500">
            <Plus size={16} />
          </button>
          <button onClick={() => setIsAdding(false)} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full text-slate-400">
            <X size={16} />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setIsAdding(true)}
          className="px-3 py-1.5 rounded-full text-sm font-medium border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600 dark:border-slate-600 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-300 flex items-center gap-1 transition-colors"
        >
          <Plus size={14} /> 新建标签
        </button>
      )}

      {/* Tools Dropdown - Replaces individual Clean/Color buttons */}
      <div className="relative" ref={toolsRef}>
        <button 
          onClick={() => setShowTools(!showTools)}
          className="w-8 h-8 rounded-full flex items-center justify-center border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 hover:text-slate-600 dark:border-slate-600 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-300 transition-colors"
          title="更多工具"
        >
          <MoreHorizontal size={16} />
        </button>

        {showTools && (
            <div className="absolute top-full right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col p-1 origin-top-right">
                {/* Clean Invalid Tags */}
                <button 
                    onClick={() => {
                        if (onCleanTags) onCleanTags();
                        setShowTools(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors w-full text-left"
                >
                    <Trash2 size={14} />
                    <span>清理无效标签</span>
                </button>

                <div className="h-px bg-slate-100 dark:bg-slate-700 my-1"></div>

                {/* Randomize Colors */}
                <button 
                    onClick={handleRandomizeColors}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors w-full text-left"
                >
                    <Wand2 size={14} className="text-purple-500" />
                    <span>随机标签颜色</span>
                </button>

                {/* Unified Color */}
                <div className="px-3 py-2">
                    <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 mb-2">
                        <div className="w-3.5 h-3.5 rounded-full bg-gradient-to-br from-red-500 via-green-500 to-blue-500" />
                        <span>统一颜色</span>
                    </div>
                    <div className="grid grid-cols-6 gap-1.5">
                        {COLORS.slice(0, 12).map(c => (
                            <button 
                                key={c}
                                className="w-5 h-5 rounded-full hover:scale-110 transition-transform shadow-sm ring-1 ring-black/5 dark:ring-white/10"
                                style={{ backgroundColor: c }}
                                onClick={() => {
                                    if (onUpdateAllTagsColor) onUpdateAllTagsColor(c);
                                    setShowTools(false);
                                }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default TagManager;
